import { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { toast } from 'sonner';
import { formatDateToISO } from '@/lib/date-utils';
import { uuidv4 } from '@/lib/utils';
import {
  saveActiveFasting,
  getActiveFasting,
  clearActiveFasting,
  saveEntry,
  FastingEntry,
} from '@/lib/storage';
import { loadSettings } from '@/lib/settings';
import { supabase } from '@/integrations/supabase/client';

/**
 * Single shared source of truth for fasting state.
 * Anything that logs food should call handleFoodLogged() so the fast
 * closes and restarts consistently, no matter which screen logged it.
 */
interface FastingContextValue {
  startTime: Date | null;
  elapsed: number;
  isActive: boolean;
  loading: boolean;
  goal: number;
  start: (from?: Date) => void;
  stop: () => Promise<number | null>;
  handleFoodLogged: () => Promise<number | null>;
}

const FastingContext = createContext<FastingContextValue | undefined>(undefined);

export const FastingProvider = ({ children }: { children: ReactNode }) => {
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [loading, setLoading] = useState(true);
  const [goal, setGoal] = useState(() => loadSettings().fastingGoal);
  const busyRef = useRef(false);

  useEffect(() => {
    const active = getActiveFasting();
    if (active?.startTime) {
      const start = new Date(active.startTime);
      if (!isNaN(start.getTime())) {
        setStartTime(start);
        setElapsed(Math.max(0, Date.now() - start.getTime()));
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!startTime) return;
    const interval = window.setInterval(() => {
      setElapsed(Math.max(0, Date.now() - startTime.getTime()));
    }, 1000);
    return () => clearInterval(interval);
  }, [startTime]);

  const start = useCallback((from?: Date) => {
    const now = from ?? new Date();
    setStartTime(now);
    setElapsed(0);
    saveActiveFasting({ startTime: now.toISOString() });
  }, []);

  const stop = useCallback(async (): Promise<number | null> => {
    if (!startTime || busyRef.current) return null;
    busyRef.current = true;
    const endTime = new Date();
    const duration = endTime.getTime() - startTime.getTime();
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user?.id || 'offline-user';
      const entry: FastingEntry = {
        id: uuidv4(),
        type: 'fasting',
        timestamp: startTime.toISOString(),
        date: formatDateToISO(startTime),
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        duration,
        user_id: userId,
      };
      await saveEntry(entry);
      await clearActiveFasting();
    } catch (error) {
      console.error('Error stopping fast:', error);
      toast.error('Could not save that fasting session');
    } finally {
      busyRef.current = false;
    }
    setStartTime(null);
    setElapsed(0);
    return duration;
  }, [startTime]);

  /** Called after any food log: closes the current fast and opens a new window. */
  const handleFoodLogged = useCallback(async () => {
    const ended = await stop();
    start(new Date());
    return ended;
  }, [start, stop]);

  return (
    <FastingContext.Provider
      value={{ startTime, elapsed, isActive: !!startTime, loading, goal, start, stop, handleFoodLogged }}
    >
      {children}
    </FastingContext.Provider>
  );
};

export const useFasting = () => {
  const ctx = useContext(FastingContext);
  if (!ctx) throw new Error('useFasting must be used within a FastingProvider');
  return ctx;
};
